import React from 'react';
import { belief, cta, customerStory, dpp, forms, founders, intelligence, parts, trust } from '../../content';
import type { HeroUnit } from '../../heroUnits';
import { Arrow, SmartLink, Swipe, useReveal } from '../../ui';
import { FormsExplainer, PartsExplainer, type ExplainerProps } from '../../explainers';
import { GenericVsGrounded } from '../../ai';
import { BookingWidget } from '../../booking/BookingWidget';

// Variant C, everything after the story. One idea per screen, grounds alternate paper / alt / indigo.

const Check = ({ dark }: { dark?: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="mt-[3px] shrink-0">
    <path d="M4 9.4 7.4 12.6 14 5.6" stroke={dark ? '#CFF710' : '#2F9E58'} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Dash = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="mt-[3px] shrink-0">
    <path d="M5 9h8" stroke="rgba(255,255,255,.4)" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* ---------- 4. Intelligent products ---------- */
export function Expert({ unit }: { unit: HeroUnit }) {
  const ref = useReveal<HTMLElement>();
  const [a, b] = intelligence.title;
  return (
    <section className="section bg-bm-alt vc-expert" id="expert" ref={ref}>
      <div className="wrap">
        <div className="vc-head" data-reveal>
          <h2 className="t-h2">
            {a} <Swipe>{b.replace(/\.$/, '')}</Swipe>.
          </h2>
          <p className="t-lede vc-head-lede">
            It knows the unit&rsquo;s build and history, and cites the page it used.
          </p>
        </div>
        <div className="mt-10 lg:mt-12" data-reveal>
          <GenericVsGrounded tone="light" unit={unit} />
        </div>
        <div className="vc-expert-foot" data-reveal>
          <SmartLink href="/platform/ai" className="vc-link">
            How the AI works <Arrow />
          </SmartLink>
        </div>
      </div>
    </section>
  );
}

/* ---------- 5. Revenue + operations ---------- */
type Pair = { eyebrow: string; title: string[]; lede: string; href: string; link: string; Ex: React.ComponentType<ExplainerProps> };
const PAIR: Pair[] = [
  {
    eyebrow: parts.eyebrow,
    title: parts.title,
    lede: 'Only the parts that fit that serial, bought from you or their dealer.',
    href: '/platform/parts',
    link: 'Spares and accessories',
    Ex: PartsExplainer,
  },
  {
    eyebrow: forms.eyebrow,
    title: forms.title,
    lede: 'Your commissioning or inspection sheet, signed on a phone and filed on that serial.',
    href: '/platform/forms',
    link: 'Forms and signatures',
    Ex: FormsExplainer,
  },
];

export function RevenueOps({ unit }: { unit: HeroUnit }) {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="section vc-pair-sec" id="parts-forms" ref={ref}>
      <div className="wrap">
        <h2 className="t-h2 max-w-[26ch]" data-reveal>
          Parts sales and signed forms, filed on the <Swipe>serial</Swipe>.
        </h2>
        <div className="vc-pair">
          {PAIR.map((p, k) => (
            <article key={p.href} className="vc-pair-card" data-reveal style={{ ['--reveal-delay' as string]: `${k * 0.08}s` }}>
              <h3 className="vc-pair-t">
                {p.title[0]} <span className="text-bm-emerald">{p.title[1]}</span>
              </h3>
              <p className="vc-pair-d">{p.lede}</p>
              <div className="frame mt-7">
                <div>
                  <p.Ex tone="light" unit={unit} />
                </div>
              </div>
              <SmartLink href={p.href} className="vc-link mt-6">
                {p.link} <Arrow />
              </SmartLink>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- 6. Compliance and convenience ---------- */
export function Compliance() {
  const ref = useReveal<HTMLElement>();
  const { minimum, better } = dpp.contrast;
  return (
    <section className="on-dark vc-dpp" id="compliance" ref={ref}>
      <div className="wrap section">
        <div className="vc-head vc-head-stack" data-reveal>
          <h2 className="t-h2">
            Passport data for each <Swipe>unit</Swipe>, kept current.
          </h2>
          <p className="t-lede vc-head-lede">
            Passports and right to repair are coming. Meet both with a product that is easier to own.
          </p>
        </div>
        <div className="vc-vs" data-reveal role="table" aria-label="The minimum compared with compliance and convenience">
          <div className="vc-vs-row vc-vs-hd" role="row">
            <span role="columnheader">{minimum.label}</span>
            <span role="columnheader">{better.label}</span>
          </div>
          {minimum.points.map((t, k) => (
            <div className="vc-vs-row" role="row" key={t}>
              <span role="cell" className="vc-vs-min">
                <Dash />
                {t}
              </span>
              <span role="cell" className="vc-vs-better">
                <Check dark />
                {better.points[k]}
              </span>
            </div>
          ))}
        </div>
        <div className="vc-vs-m" data-reveal>
          <div className="vc-vs-m-min">
            <div className="vc-vs-m-h">{minimum.label}</div>
            <p>{minimum.points.map((t, k) => (k ? t.charAt(0).toLowerCase() + t.slice(1) : t)).join(', ')}.</p>
          </div>
          <div className="vc-vs-m-better">
            <div className="vc-vs-m-h">{better.label}</div>
            <ul>
              {better.points.map((t) => (
                <li key={t}>
                  <Check dark />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <SmartLink href="/platform/digital-product-passport" className="vc-link vc-link-dark mt-10">
          Digital Product Passport <Arrow />
        </SmartLink>
      </div>
    </section>
  );
}

/* ---------- 2. What it pays back ---------- */
export function Belief() {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="vc-belief" ref={ref}>
      <div className="wrap" data-reveal>
        <h2 className="vc-belief-t">
          {belief.title[0]} <Swipe draw>{belief.swipe}</Swipe>.
        </h2>
        <ul className="vc-belief-l">
          {belief.items.map((x) => (
            <li key={x.t}>
              <h3>{x.t}</h3>
              <p>{x.d}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// The pledge's first sentence is the promise; the rest is behind the clause link.
const firstSentence = (t: string) => t.match(/^.*?[.!?](?=\s|$)/)?.[0] ?? t;

export function TrustBand() {
  const ref = useReveal<HTMLElement>();
  const lead = trust.pledges.slice(0, 2);
  return (
    <section className="on-dark vc-trust" id="trust" ref={ref}>
      <div className="wrap section">
        <div className="vc-head" data-reveal>
          <h2 className="t-h2">
            Your data, your customers, <Swipe>your brand</Swipe>.
          </h2>
          <p className="t-lede vc-head-lede">Owners see your brand. The Customer Terms make the data yours.</p>
        </div>
        <div className="vc-pl-lead">
          {lead.map((p, k) => (
            <div key={p.t} data-reveal style={{ ['--reveal-delay' as string]: `${k * 0.06}s` }}>
              <h3>{p.t}</h3>
              <p>{firstSentence(p.d)}</p>
              <SmartLink href={p.href} className="vc-src-link">
                {p.src} <Arrow />
              </SmartLink>
            </div>
          ))}
        </div>
        <SmartLink href="/trust/" className="vc-link vc-link-dark mt-10">
          Trust Centre <Arrow />
        </SmartLink>
      </div>
    </section>
  );
}

/* ---------- 9. Proof: customer, team, price ---------- */
export function Proof() {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="section bg-bm-alt vc-proof-sec" id="proof" ref={ref}>
      <div className="wrap">
        <div className="vc-story-card" data-reveal>
          <div>
            <p className="vc-label">{customerStory.who}</p>
            <h2 className="vc-quote mt-4">{customerStory.headline}</h2>
          </div>
          <div>
            <p className="text-[16px] leading-relaxed text-bm-ink">
              {customerStory.descriptor}. A code on every packing slip first, then a Digital Product Passport for each part.
            </p>
            <dl className="vc-facts mt-6">
              {customerStory.facts.slice(0, 3).map((f) => (
                <div key={f.k}>
                  <dt>{f.k}</dt>
                  <dd>{f.v}</dd>
                </div>
              ))}
            </dl>
            <SmartLink href="/customers" className="vc-link mt-6">
              Read the story <Arrow />
            </SmartLink>
          </div>
        </div>

        <div className="vc-team" data-reveal>
          <h2 className="vc-team-t">
            {founders.title[0]} <Swipe>{founders.title[1].replace(/\.$/, '')}</Swipe>.
          </h2>
          <dl className="vc-creds">
            {founders.credentials.map((c) => (
              <div key={c.k}>
                <dt className="font-mono">{c.k}</dt>
                <dd>{c.v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="vc-price" data-reveal>
          <SmartLink href="/pricing" className="vc-link">
            See pricing <Arrow />
          </SmartLink>
        </div>
      </div>
    </section>
  );
}

/* ---------- 10. Book ---------- */
// The last screen. A buyer's real questions here: what happens in the 30 minutes, what do I prepare,
// and what do I leave with. Answer them as an agenda, beside the calendar.
const AGENDA = [
  { at: '0', t: 'Your product', d: 'Name one model you make.' },
  { at: '5', t: 'Its life, live', d: 'On your product, in your brand.' },
  { at: '20', t: 'Your systems', d: 'What it reads from your ERP and writes back.' },
  { at: '25', t: 'A straight answer', d: 'Fit, effort and cost.' },
];

export function Book() {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="section vc-book" ref={ref} id="book">
      <div className="wrap vc-final">
        <div data-reveal>
          <h2 className="t-h2">
            Let&rsquo;s <Swipe>talk</Swipe>.
          </h2>
          <p className="t-lede mt-5 max-w-[42ch]">30 minutes on one of your products, with the people who build it.</p>
          <ol className="vc-agenda" aria-label="What happens in the 30 minutes">
            {AGENDA.map((a) => (
              <li key={a.at}>
                <span className="vc-agenda-at">
                  {a.at}
                  <small>min</small>
                </span>
                <span>
                  <b>{a.t}</b>
                  <span>{a.d}</span>
                </span>
              </li>
            ))}
          </ol>
          <SmartLink href={cta.secondaryHref} className="vc-quiet mt-8 inline-block">
            Not ready to talk? {cta.secondary}
          </SmartLink>
        </div>
        <div className="vc-final-widget" data-reveal>
          <BookingWidget compact source="home-c" />
        </div>
      </div>
      <div className="wrap">
        <ShareLine />
      </div>
    </section>
  );
}

// Buyers often forward the page to a colleague: make that one click.
function ShareLine() {
  const [copied, setCopied] = React.useState(false);
  const url = typeof window === 'undefined' ? 'https://www.brandedmark.com/' : `${window.location.origin}${window.location.pathname}`;
  const subject = 'Worth 30 minutes? One record for every unit we make';
  const body = `This looks relevant to how we handle aftersales, warranty and parts:\n\n${url}\n\nThey do a 30 minute call where we bring one of our products.`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2400);
    } catch {
      window.prompt('Copy this link', url);
    }
  };
  return (
    <div className="vc-share">
      <p>Not your call? Send this to whoever runs aftersales.</p>
      <div className="vc-share-acts">
        <button type="button" className="vc-share-btn" onClick={copy} aria-live="polite">
          {copied ? 'Link copied' : 'Copy link'}
        </button>
        <a className="vc-share-btn" href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}>
          Email it
        </a>
      </div>
    </div>
  );
}

export const AFTER_CSS = `
.vc-path { list-style: none; margin: 0 0 14px; padding: 0; width: 100%; display: grid; gap: 10px; counter-reset: p; font-size: 15px; line-height: 1.35; color: #241E61; font-weight: 600; }
.vc-path li { counter-increment: p; display: flex; align-items: center; gap: 10px; }
.vc-path li::before { content: counter(p); flex: none; display: inline-grid; place-items: center; width: 24px; height: 24px; border-radius: 99px; background: #241E61; color: #CFF710; font-size: 12px; font-weight: 700; }
@media (min-width: 900px) { .vc-path { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; } .vc-path li { border-top: 2px solid; border-image: linear-gradient(95deg,#3FAE6C,#CFF710) 1; padding-top: 14px; } }

.vc-belief { padding: clamp(64px, 8vw, 120px) 0; background: #fff; border-top: 1px solid rgba(36,30,97,.08); }
.vc-belief-t { margin: 0; max-width: 22ch; font-weight: 800; font-size: clamp(34px, 5vw, 68px); line-height: 1.02; letter-spacing: -.04em; color: #241E61; }
.vc-belief-l { list-style: none; margin: clamp(36px, 4vw, 56px) 0 0; padding: 0; display: grid; gap: 28px; }
.vc-belief-l li { border-top: 2px solid #241E61; padding-top: 18px; }
.vc-belief-l h3 { margin: 0; font-weight: 700; font-size: 20px; line-height: 1.25; letter-spacing: -.02em; color: #241E61; }
.vc-belief-l p { margin: 8px 0 0; font-size: 15.5px; line-height: 1.6; color: #67648F; max-width: 40ch; }
@media (min-width: 760px) { .vc-belief-l { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 36px; } }

.v2 .vc > section.section, .v2 .vc > section > .section { padding-top: clamp(64px, 7.2vw, 104px); padding-bottom: clamp(64px, 7.2vw, 104px); }
.vc-head { display: grid; gap: 20px; }
@media (min-width: 1024px) { .vc-head { grid-template-columns: minmax(0, 1.3fr) minmax(0, .9fr); gap: 64px; align-items: end; } }
.vc-head-lede { margin: 0; max-width: 48ch; }
@media (min-width: 1024px) { .vc-head.vc-head-stack { grid-template-columns: 1fr; gap: 20px; } .vc-head-stack .vc-head-lede { max-width: 60ch; } }
.vc-label { margin: 0; font-size: 14px; font-weight: 600; color: var(--bm-emerald); }
.vc-label-dark { color: var(--bm-lime); }
.vc-link-dark { color: #fff; border-color: rgba(255,255,255,.3); }
.vc-link-dark:hover { border-color: var(--bm-lime); }

@media (max-width: 759px) { .vc-expert .ai-vs-gen .ai-vs-facts { display: none; } }
.vc-expert-foot { margin-top: 28px; padding-top: 22px; border-top: 1px solid var(--bm-line); display: flex; flex-wrap: wrap; gap: 14px 40px; align-items: center; justify-content: space-between; }
.vc-expert-foot p { margin: 0; max-width: 64ch; font-size: 15px; line-height: 1.55; color: var(--bm-muted); }

.vc-pair { margin-top: clamp(32px, 4vw, 52px); display: grid; gap: 56px; }
@media (min-width: 960px) { .vc-pair { grid-template-columns: 1fr 1fr; gap: 48px; } }
.vc-pair-card { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; padding-top: 22px; border-top: 1.5px solid var(--bm-indigo); }
.vc-pair-card > .frame { align-self: stretch; }
.vc-pair-top { display: flex; align-items: center; gap: 12px; }
.vc-pair-eb { font-size: 14px; font-weight: 600; color: var(--bm-emerald); }
.vc-pair-t { margin: 0; font-weight: 800; font-size: clamp(24px, 2.3vw, 30px); line-height: 1.12; letter-spacing: -.03em; color: var(--bm-indigo); text-wrap: balance; }
.vc-pair-d { margin: 12px 0 0; font-size: 16px; line-height: 1.6; color: var(--bm-muted); max-width: 52ch; }

.vc-dpp { background: var(--bm-indigo); }
.vc-vs { margin-top: clamp(36px, 4vw, 56px); border-radius: 18px; overflow: hidden; box-shadow: 0 0 0 1px rgba(255,255,255,.14); }
.vc-vs-row { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr); }
.vc-vs-row > span { display: flex; gap: 12px; align-items: flex-start; padding: 18px 28px; font-size: 16.5px; line-height: 1.4; border-top: 1px solid rgba(255,255,255,.1); }
.vc-vs-min { background: rgba(0,0,0,.16); color: rgba(255,255,255,.55); }
.vc-vs-better { background: #2D2775; color: #fff; font-weight: 500; }
.vc-vs-hd > span { border-top: 0; padding-top: 22px; padding-bottom: 16px; font-size: 14px; font-weight: 700; color: rgba(255,255,255,.6); background: rgba(0,0,0,.16); }
.vc-vs-hd > span:last-child { color: #fff; background: #2D2775; position: relative; }
.vc-vs-hd > span:last-child::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 5px; background: var(--bm-gradient); }
.vc-vs-m { display: none; }
@media (max-width: 720px) {
  .vc-vs { display: none; }
  .vc-vs-m { display: block; margin-top: 36px; border-radius: 16px; overflow: hidden; box-shadow: 0 0 0 1px rgba(255,255,255,.14); }
  .vc-vs-m-min { padding: 18px 18px 16px; background: rgba(0,0,0,.16); }
  .vc-vs-m-min p { margin: 6px 0 0; font-size: 14px; line-height: 1.5; color: rgba(255,255,255,.5); }
  .vc-vs-m-better { position: relative; padding: 22px 18px 8px; background: #2D2775; }
  .vc-vs-m-better::before { content: ''; position: absolute; left: 0; right: 0; top: 0; height: 5px; background: var(--bm-gradient); }
  .vc-vs-m-h { font-size: 13.5px; font-weight: 700; color: rgba(255,255,255,.6); }
  .vc-vs-m-better .vc-vs-m-h { color: #fff; }
  .vc-vs-m ul { list-style: none; margin: 8px 0 0; padding: 0; }
  .vc-vs-m li { display: flex; gap: 10px; padding: 12px 0; border-top: 1px solid rgba(255,255,255,.1); font-size: 15.5px; line-height: 1.4; color: #fff; font-weight: 500; }
}

.vc-pl-lead { margin-top: clamp(36px, 4vw, 56px); display: grid; gap: 1px; background: rgba(255,255,255,.14); border-top: 1px solid rgba(255,255,255,.14); border-bottom: 1px solid rgba(255,255,255,.14); }
@media (min-width: 900px) { .vc-pl-lead { grid-template-columns: 1fr 1fr; } }
.vc-pl-lead > div { background: var(--bm-indigo); padding: 28px 0; }
@media (min-width: 900px) { .vc-pl-lead > div:first-child { padding-right: 40px; } .vc-pl-lead > div:last-child { padding-left: 40px; } }
.vc-pl-lead h3 { margin: 0; font-size: clamp(22px, 2vw, 27px); font-weight: 800; line-height: 1.15; letter-spacing: -.025em; color: #fff; }
.vc-pl-lead p { margin: 10px 0 0; font-size: 15.5px; line-height: 1.6; color: rgba(255,255,255,.72); max-width: 54ch; }
.vc-pl-rest { list-style: none; margin: 0; padding: 0; display: grid; }
@media (min-width: 700px) { .vc-pl-rest { grid-template-columns: 1fr 1fr; column-gap: 40px; } }
@media (min-width: 1100px) { .vc-pl-rest { grid-template-columns: repeat(4, 1fr); } }
.vc-pl-rest li { padding: 20px 0; border-bottom: 1px solid rgba(255,255,255,.14); display: flex; flex-direction: column; justify-content: space-between; gap: 2px; }
.vc-pl-rest li > span { font-size: 16px; font-weight: 700; line-height: 1.3; color: #fff; letter-spacing: -.01em; }
.vc-pl-rest .vc-src-link { margin-top: 8px; font-size: 13px; }

.vc-story-card { display: grid; gap: 32px; }
.vc-proof-sec .vc-quote { font-size: clamp(30px, 3.1vw, 42px); }
@media (min-width: 1024px) { .vc-story-card { grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); gap: 80px; align-items: end; } }
.vc-facts { grid-template-columns: repeat(3, 1fr) !important; }
@media (max-width: 759px) {
  .vc-proof-sec .vc-facts { display: none; }
  .vc-team .vc-creds div { padding: 14px 0; grid-template-columns: 56px minmax(0, 1fr); }
  .vc-team .vc-creds dd { font-size: 17px; }
}
@media (max-width: 520px) { .vc-facts { grid-template-columns: 1fr !important; } .vc-facts div { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; } .vc-facts dd { text-align: right; font-size: 15px; } }
.vc-team { margin-top: clamp(40px, 5vw, 64px); padding-top: 32px; border-top: 1.5px solid var(--bm-indigo); display: grid; gap: 24px; }
@media (min-width: 1024px) { .vc-team { grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr); gap: 80px; } }
.vc-team-t { margin: 0; font-weight: 800; font-size: clamp(28px, 3vw, 40px); line-height: 1.08; letter-spacing: -.035em; color: var(--bm-indigo); text-wrap: balance; }
.vc-team .vc-creds { border-top: 0; }
.vc-team .vc-creds div:first-child { padding-top: 4px; }
.vc-proof-sec .vc-price { margin-top: clamp(40px, 5vw, 64px); padding: 22px 0; display: flex; flex-wrap: wrap; gap: 14px 32px; align-items: center; justify-content: space-between; border-top: 1px solid var(--bm-line); border-bottom: 1px solid var(--bm-line); }
.vc-proof-sec .vc-price p { margin: 0; font-size: 17px; color: var(--bm-ink); }
.vc-proof-sec .vc-price b { color: var(--bm-indigo); font-weight: 700; }
.vc-book .vc-final { margin-top: 0; }
.vc-agenda { position: relative; margin: 36px 0 0; padding: 0; list-style: none; display: grid; gap: 22px; max-width: 520px; }
.vc-agenda::before { content: ''; position: absolute; left: 27px; top: 10px; bottom: 10px; width: 2px; border-radius: 2px; background: linear-gradient(180deg, #3FAE6C, #8FD53A 55%, #CFF710); }
.vc-agenda li { position: relative; display: grid; grid-template-columns: 56px minmax(0, 1fr); gap: 18px; align-items: start; }
.vc-agenda-at { position: relative; z-index: 1; display: grid; place-items: center; align-content: center; width: 56px; height: 56px; border-radius: 16px; background: #fff; box-shadow: inset 0 0 0 1px var(--bm-line), 0 6px 18px -10px rgba(36,30,97,.35); font-weight: 800; font-size: 19px; line-height: 1; letter-spacing: -.03em; color: var(--bm-indigo); font-variant-numeric: tabular-nums; }
.vc-agenda-at small { margin-top: 3px; font-size: 10.5px; font-weight: 600; letter-spacing: .02em; color: var(--bm-muted); }
.vc-agenda li > span:last-child { padding-top: 6px; display: grid; gap: 3px; }
.vc-agenda b { font-size: 17px; font-weight: 700; letter-spacing: -.01em; color: var(--bm-indigo); }
.vc-agenda li > span:last-child > span { font-size: 15.5px; line-height: 1.5; color: var(--bm-muted); }
@media (max-width: 640px) { .vc-agenda { gap: 18px; margin-top: 28px; } .vc-agenda li { grid-template-columns: 48px minmax(0, 1fr); gap: 14px; } .vc-agenda-at { width: 48px; height: 48px; border-radius: 14px; font-size: 17px; } .vc-agenda::before { left: 23px; } .vc-agenda b { font-size: 16px; } .vc-agenda li > span:last-child { padding-top: 3px; } .vc-agenda li > span:last-child > span { font-size: 15px; } }
.vc-share { margin-top: clamp(40px, 5vw, 64px); padding-top: 22px; border-top: 1px solid var(--bm-line); display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px 24px; }
.vc-share p { margin: 0; font-size: 15.5px; color: var(--bm-muted); }
.vc-share-acts { display: flex; gap: 8px; }
.vc-share-btn { display: inline-flex; align-items: center; min-height: 40px; padding: 0 16px; border-radius: 999px; font-size: 14.5px; font-weight: 600; color: var(--bm-indigo); box-shadow: inset 0 0 0 1px var(--bm-line); background: #fff; cursor: pointer; transition: box-shadow .18s var(--bm-ease); }
.vc-share-btn:hover { box-shadow: inset 0 0 0 1.5px var(--bm-indigo); }
.vc-share-btn:focus-visible { outline: 2px solid var(--bm-emerald); outline-offset: 2px; }
.vc-sch-entry { display: none; }
@media (max-width: 759px) {
  .vc-sch { padding: 28px 0; }
  .vc-sch .vc-ch-t { font-size: 28px; margin: 10px 0 12px; }
  .vc-sch .vc-ch-body { font-size: 16px; line-height: 1.55; }
  .vc-sch .vc-gain { margin-top: 14px; padding-top: 12px; font-size: 14.5px; }
  .vc-sch .vc-also { font-size: 14px; margin-top: 10px; }
  .vc-story-intro .t-lede { font-size: 17px; }
  .vc-sch-vis .vc-record { display: none; }
  .vc-sch-vis { margin-top: 18px; gap: 14px; }
  .vc-sch-scene { zoom: .76; }
  .vc-sch-entry { position: relative; z-index: 2; width: calc(100% - 28px) !important; margin-bottom: -40px; box-shadow: 0 18px 36px -18px rgba(24,19,67,.55); }
  .vc-sch-scene .vc-paper { height: auto; width: 300px; padding: 64px 18px 20px; border-radius: 22px; }
  .vc-sch-entry { display: grid; gap: 2px; width: 100%; padding: 12px 14px; border-radius: 12px; background: var(--bm-indigo); color: #fff; }
  .vc-sch-entry-l { font-size: 11px; font-weight: 600; letter-spacing: .04em; color: var(--bm-lime); }
  .vc-sch-entry-t { font-size: 15px; font-weight: 700; }
  .vc-sch-entry-m { font-size: 11.5px; color: rgba(255,255,255,.62); }
}
`;
